"use client"

import { Button } from "@/components/ui/button"
import { ArrowRight, Shield, TrendingUp, Rocket } from "lucide-react"
import { Icons8Icon } from "@/components/ui/icons8-icon"
import Link from "next/link"
import { TKSPresaleBoxV2 } from "@/components/tks-presale-box-v2"

export function HeroSection() {
  return (
    <section className="relative overflow-hidden flex items-center pt-32 pb-16 md:pt-36 md:pb-24">
      {/* Subtle center gradient for text readability over starfield - only in dark mode */}
      <div className="absolute inset-0 z-[1] pointer-events-none dark:block hidden" style={{
        background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(10,10,15,0.7) 0%, transparent 80%)'
      }} />

      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left side - Text content */}
          <div className="text-center lg:text-left">
            {/* Pill badge — hardcoded fallback color for SSR */}
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-5 py-2 text-sm font-medium" style={{ color: '#7ed957' }}>
              <Icons8Icon name="lightning-bolt" size="xs" />
              Decentralized Token Launchpad
            </div>

            {/* Hero heading — hardcoded colors ensure text is always visible for Google */}
            <h1 className="mb-6 text-5xl font-bold tracking-tight text-balance sm:text-6xl lg:text-7xl">
              <span className="text-foreground" style={{ color: '#111' }}>Launch Your</span>
              <br />
              <span style={{ color: '#7ed957' }}>
                Token Presale
              </span>
            </h1>

            {/* Subtitle — hardcoded fallback color */}
            <p className="mb-10 text-lg text-muted-foreground text-balance md:text-xl max-w-2xl mx-auto lg:mx-0 lg:max-w-none leading-relaxed" style={{ color: '#666' }}>
              The trusted launchpad for crypto projects.
              Create tokens, launch presales, and grow your community with full transparency.
            </p>

            {/* CTA Buttons — hardcoded fallback colors */}
            <div className="flex flex-col sm:flex-row items-center lg:justify-start justify-center gap-4 mb-10 lg:mb-0">
              <Link href="/presales">
                <Button size="xl" className="gap-2 dark:bg-white dark:text-black dark:hover:bg-gray-100 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8" style={{ backgroundColor: '#7ed957', color: '#000' }}>
                  Explore Presales
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link href="/create-presale">
                <Button variant="outline" size="xl" className="dark:border-white/30 dark:text-white dark:hover:bg-white/10 border-border text-foreground hover:bg-accent rounded-full px-8" style={{ borderColor: '#ccc', color: '#333' }}>
                  Create Presale
                </Button>
              </Link>
            </div>
          </div>

          {/* Right side - TKS Presale Buy Box (client-only, skipped during SSR) */}
          <div className="flex items-center justify-center lg:justify-end">
            <TKSPresaleBoxV2 />
          </div>
        </div>

        {/* Stats with Lucide icons — hardcoded fallback colors */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto lg:max-w-none lg:mt-16 mt-8">
          <div className="flex flex-col items-center gap-2 p-4 group">
            <div className="flex items-center justify-center h-14 w-14 rounded-full bg-[#7ed957]/10 group-hover:bg-[#7ed957]/20 transition-all group-hover:scale-110">
              <Shield className="h-7 w-7 text-[#7ed957]" />
            </div>
            <div className="text-3xl font-bold tracking-tight text-foreground" style={{ color: '#111' }}>Audited</div>
            <div className="text-sm text-muted-foreground" style={{ color: '#888' }}>Smart Contracts</div>
          </div>
          <div className="flex flex-col items-center gap-2 p-4 group">
            <div className="flex items-center justify-center h-14 w-14 rounded-full bg-[#7ed957]/10 group-hover:bg-[#7ed957]/20 transition-all group-hover:scale-110">
              <TrendingUp className="h-7 w-7 text-[#7ed957]" />
            </div>
            <div className="text-3xl font-bold tracking-tight text-foreground" style={{ color: '#111' }}>0%</div>
            <div className="text-sm text-muted-foreground" style={{ color: '#888' }}>Fee on Raised Funds</div>
          </div>
          <div className="flex flex-col items-center gap-2 p-4 group">
            <div className="flex items-center justify-center h-14 w-14 rounded-full bg-[#7ed957]/10 group-hover:bg-[#7ed957]/20 transition-all group-hover:scale-110">
              <Rocket className="h-7 w-7 text-[#7ed957]" />
            </div>
            <div className="text-3xl font-bold tracking-tight text-foreground" style={{ color: '#111' }}>5+</div>
            <div className="text-sm text-muted-foreground" style={{ color: '#888' }}>Chains Supported</div>
          </div>
        </div>
      </div>
    </section>
  )
}
